import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import * as d3 from 'd3';

const ElectricityEntityDrawer = ({ entityDetails, chartRef }) => {
  const [currentUsage, setCurrentUsage] = useState(0);
  const [realtimeData, setRealtimeData] = useState([]);
  const realtimeChartRef = useRef(null);
  const intervalRef = useRef(null);

  // Mock electricity data
  const electricityData = {
    meters: [
      {
        id: 'ELEC-METER-001',
        location: 'Main Building',
        status: 'Active',
        lastUpdate: '2024-01-15T10:30:00Z',
        metrics: {
          currentUsage: 45.2,
          voltage: 240,
          powerFactor: 0.95
        }
      }
    ],
    totalMetrics: {
      totalConsumption: 156.5,
      averageUsage: 52.2,
      peakUsage: 89.3
    },
    // 24-hour consumption data (hourly intervals)
    dailyConsumptionData: [
      { hour: '00:00', consumption: 12.5 },
      { hour: '01:00', consumption: 8.2 },
      { hour: '02:00', consumption: 6.1 },
      { hour: '03:00', consumption: 5.8 },
      { hour: '04:00', consumption: 6.3 },
      { hour: '05:00', consumption: 7.9 },
      { hour: '06:00', consumption: 12.4 },
      { hour: '07:00', consumption: 18.7 },
      { hour: '08:00', consumption: 25.3 },
      { hour: '09:00', consumption: 32.1 },
      { hour: '10:00', consumption: 38.9 },
      { hour: '11:00', consumption: 42.6 },
      { hour: '12:00', consumption: 45.2 },
      { hour: '13:00', consumption: 48.7 },
      { hour: '14:00', consumption: 52.3 },
      { hour: '15:00', consumption: 56.8 },
      { hour: '16:00', consumption: 61.4 },
      { hour: '17:00', consumption: 67.2 },
      { hour: '18:00', consumption: 72.9 },
      { hour: '19:00', consumption: 78.5 },
      { hour: '20:00', consumption: 82.1 },
      { hour: '21:00', consumption: 76.8 },
      { hour: '22:00', consumption: 68.4 },
      { hour: '23:00', consumption: 45.2 }
    ]
  };

  // Generate random current usage for simulation
  const generateRandomUsage = () => {
    const baseUsage = 45; // Base usage in kW
    const variation = (Math.random() - 0.5) * 20; // ±10 kW variation
    return Math.max(0, baseUsage + variation);
  };

  // Update current usage every second
  useEffect(() => {
    const updateUsage = () => {
      const newUsage = generateRandomUsage();
      setCurrentUsage(newUsage);
      
      // Add to realtime data (keep last 2 minutes = 30 data points)
      const now = new Date();
      const newDataPoint = {
        time: now.toLocaleTimeString('en-US', { 
          hour12: false, 
          hour: '2-digit', 
          minute: '2-digit', 
          second: '2-digit' 
        }),
        usage: newUsage,
        timestamp: now.getTime()
      };
      
      setRealtimeData(prevData => {
        const updatedData = [...prevData, newDataPoint];
        // Keep only last 30 data points (2 minutes at 1-second intervals)
        return updatedData.slice(-30);
      });
    };

    // Initial update
    updateUsage();
    
    // Set up interval
    intervalRef.current = setInterval(updateUsage, 1000);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Render 24-hour consumption chart
  useEffect(() => {
    if (chartRef.current && electricityData.dailyConsumptionData.length > 0) {
      renderDailyConsumptionChart();
    }
  }, [electricityData]);

  // Render realtime chart
  useEffect(() => {
    if (realtimeChartRef.current && realtimeData.length > 0) {
      renderRealtimeChart();
    }
  }, [realtimeData]);

  const renderDailyConsumptionChart = () => {
    // Clear previous chart
    d3.select(chartRef.current).selectAll('*').remove();

    const width = 400;
    const height = 300;
    const margin = { top: 20, right: 20, bottom: 60, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const data = electricityData.dailyConsumptionData;

    // Scales
    const xScale = d3.scaleBand()
      .domain(data.map(d => d.hour))
      .range([0, chartWidth])
      .padding(0.1);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.consumption)])
      .range([chartHeight, 0]);

    // Color scale based on consumption levels
    const colorScale = d3.scaleLinear()
      .domain([0, 30, 60, 90])
      .range(['#4CAF50', '#FF9800', '#F44336', '#9C27B0']);

    // X-axis
    svg.append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .style('font-size', '10px');

    // Y-axis
    svg.append('g')
      .call(d3.axisLeft(yScale))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -40)
      .attr('x', -chartHeight / 2)
      .attr('text-anchor', 'middle')
      .text('Consumption (kWh)')
      .style('font-size', '12px');

    // Bars
    svg.selectAll('rect')
      .data(data)
      .enter()
      .append('rect')
      .attr('x', d => xScale(d.hour))
      .attr('y', d => yScale(d.consumption))
      .attr('width', xScale.bandwidth())
      .attr('height', d => chartHeight - yScale(d.consumption))
      .attr('fill', d => colorScale(d.consumption))
      .attr('stroke', 'white')
      .attr('stroke-width', 1)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('opacity', 0.8);
        
        // Show tooltip
        const tooltip = d3.select(chartRef.current)
          .append('div')
          .attr('class', 'tooltip')
          .style('opacity', 0)
          .style('position', 'absolute')
          .style('background', 'rgba(0, 0, 0, 0.8)')
          .style('color', 'white')
          .style('padding', '8px')
          .style('border-radius', '4px')
          .style('font-size', '12px')
          .style('pointer-events', 'none');

        tooltip.transition()
          .duration(200)
          .style('opacity', 0.9);
        
        tooltip.html(`
          <strong>Time: ${d.hour}</strong><br/>
          Consumption: ${d.consumption} kWh
        `)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('opacity', 1);
        
        d3.select(chartRef.current).selectAll('.tooltip').remove();
      });

    // Add title
    svg.append('text')
      .attr('x', chartWidth / 2)
      .attr('y', -5)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('24-Hour Electricity Consumption');
  };

  const renderRealtimeChart = () => {
    // Clear previous chart
    d3.select(realtimeChartRef.current).selectAll('*').remove();

    const width = 400;
    const height = 200;
    const margin = { top: 20, right: 20, bottom: 40, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const svg = d3.select(realtimeChartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const data = realtimeData;

    // Scales
    const xScale = d3.scalePoint()
      .domain(data.map(d => d.time))
      .range([0, chartWidth]);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.usage)])
      .range([chartHeight, 0]);

    // Line generator
    const line = d3.line()
      .x(d => xScale(d.time))
      .y(d => yScale(d.usage))
      .curve(d3.curveMonotoneX);

    // X-axis - show only every 5th label to reduce overcrowding
    svg.append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .style('font-size', '8px')
      .style('opacity', (d, i) => i % 5 === 0 ? 1 : 0); // Show only every 5th label

    // Y-axis
    svg.append('g')
      .call(d3.axisLeft(yScale))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -40)
      .attr('x', -chartHeight / 2)
      .attr('text-anchor', 'middle')
      .text('Current Usage (kW)')
      .style('font-size', '10px');

    // Add line
    svg.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#2196F3')
      .attr('stroke-width', 3)
      .attr('d', line);

    // Add dots
    svg.selectAll('circle')
      .data(data)
      .enter()
      .append('circle')
      .attr('cx', d => xScale(d.time))
      .attr('cy', d => yScale(d.usage))
      .attr('r', 3)
      .attr('fill', '#2196F3')
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this).attr('r', 5);
        
        const tooltip = d3.select(realtimeChartRef.current)
          .append('div')
          .attr('class', 'tooltip')
          .style('opacity', 0)
          .style('position', 'absolute')
          .style('background', 'rgba(0, 0, 0, 0.8)')
          .style('color', 'white')
          .style('padding', '8px')
          .style('border-radius', '4px')
          .style('font-size', '12px')
          .style('pointer-events', 'none');

        tooltip.transition()
          .duration(200)
          .style('opacity', 0.9);
        
        tooltip.html(`
          <strong>Time: ${d.time}</strong><br/>
          Usage: ${d.usage.toFixed(1)} kW
        `)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this).attr('r', 3);
        d3.select(realtimeChartRef.current).selectAll('.tooltip').remove();
      });

    // Add title
    svg.append('text')
      .attr('x', chartWidth / 2)
      .attr('y', -5)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .text('Real-time Current Usage (Last 5 Minutes)');
  };

  return (
    <>
      {/* Electricity Meters Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <ElectricBoltIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Electricity Meters
            </Typography>
          </Box>
          
          {/* Meter List */}
          <List dense>
            {electricityData.meters.map((meter, index) => (
              <ListItem key={meter.id} divider>
                <ListItemIcon>
                  <ElectricBoltIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1">
                        {meter.id}
                      </Typography>
                      <Chip 
                        label={meter.status} 
                        size="small" 
                        color={meter.status === 'Active' ? 'success' : 'error'}
                        variant="outlined" 
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Location: {meter.location}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Last Update: {new Date(meter.lastUpdate).toLocaleString()}
                      </Typography>
                      <Box display="flex" gap={2} sx={{ mt: 1 }}>
                        <Chip 
                          label={`${meter.metrics.currentUsage} kW`} 
                          size="small" 
                          color="primary"
                          variant="outlined" 
                        />
                        <Chip 
                          label={`${meter.metrics.voltage}V`} 
                          size="small" 
                          variant="outlined" 
                        />
                        <Chip 
                          label={`PF: ${meter.metrics.powerFactor}`} 
                          size="small" 
                          color={meter.metrics.powerFactor > 0.9 ? 'success' : 'warning'}
                          variant="outlined" 
                        />
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          {/* Electricity Metrics Summary */}
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Electricity Summary (Last 24h)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" color="primary">
                    {electricityData.totalMetrics.totalConsumption}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Total kWh
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" color="success.main">
                    {electricityData.totalMetrics.averageUsage}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Avg kW
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" color="warning.main">
                    {electricityData.totalMetrics.peakUsage}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Peak kW
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Current Usage Display */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <TrendingUpIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Current Usage
            </Typography>
            <Chip 
              label="Live" 
              size="small" 
              color="success"
              sx={{ ml: 'auto' }}
            />
          </Box>
          <Box textAlign="center" sx={{ mb: 2 }}>
            <Typography variant="h3" color="primary" gutterBottom>
              {currentUsage.toFixed(1)} kW
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Updated every seconds
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Real-time Usage Chart */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <TrendingUpIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Real-time Usage
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Current usage over the last 2 minutes (updates every second)
          </Typography>
          <Box 
            ref={realtimeChartRef}
            sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '220px'
            }}
          />
        </CardContent>
      </Card>

      {/* 24-Hour Consumption Chart */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <ElectricBoltIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              24-Hour Consumption
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Hourly electricity consumption for the last 24 hours
          </Typography>
          <Box 
            ref={chartRef}
            sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '320px'
            }}
          />
        </CardContent>
      </Card>
    </>
  );
};

export default ElectricityEntityDrawer; 